import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, ArrowLeft, Loader2, BookOpen, CheckCircle2, XCircle, Award, Download, RotateCcw, ChevronDown, ChevronRight, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];

function renderContent(md) {
  return (md || "").split("\n").map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-2" />;
    if (t.startsWith("### ")) return <h4 key={i} className="font-medium text-navy mt-4 mb-1">{t.slice(4)}</h4>;
    if (t.startsWith("## ")) return <h3 key={i} className="font-medium text-lg text-navy mt-5 mb-1">{t.slice(3)}</h3>;
    if (t.startsWith("# ")) return <h2 key={i} className="font-medium text-xl text-navy mt-5 mb-2">{t.slice(2)}</h2>;
    if (t.startsWith("- ") || t.startsWith("* ")) return <li key={i} className="ml-5 list-disc text-navy/70">{t.slice(2)}</li>;
    return <p key={i} className="text-navy/70 leading-relaxed">{t}</p>;
  });
}

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [course, setCourse] = useState(null);
  const [sel, setSel] = useState([0, 0]);
  const [openMods, setOpenMods] = useState({ 0: true });
  const [tab, setTab] = useState("lessons");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [edit, setEdit] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    api.get(`/academy/courses/${id}`).then((r) => { setCourse(r.data); setErr(null); })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "No se pudo cargar el curso"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const qs = course.questions || [];
    if (Object.keys(answers).length !== qs.length) { toast.error("Responde todas las preguntas"); return; }
    setBusy(true);
    try {
      const payload = qs.map((_, i) => answers[i]);
      const r = await api.post(`/academy/courses/${id}/submit`, { answers: payload });
      setResult(r.data);
      if (r.data.passed) toast.success(`¡Aprobado con ${r.data.score}%!`);
      else toast.error(`Reprobado (${r.data.score}%). Necesitas ${r.data.pass_score}%.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  const retry = () => { setResult(null); setAnswers({}); setTab("exam"); };

  const saveLesson = async () => {
    setBusy(true);
    try {
      await api.patch(`/academy/courses/${id}/lesson`, { module_index: sel[0], lesson_index: sel[1], title: edit.title, content: edit.content });
      toast.success("Clase actualizada");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  const download = async (certId) => {
    try {
      const r = await api.get(`/academy/certificates/${certId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `certificado-omnia.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el certificado"); }
  };

  if (!course) return <OSLayout title="OMNIA Academy"><div className="py-24 flex flex-col items-center gap-4">
    {err ? (
      <div className="text-center max-w-md" data-testid="course-locked-msg">
        <Lock className="w-8 h-8 text-navy/30 mx-auto mb-3" />
        <p className="text-navy/60">{err}</p>
        <Link to="/os/academia" className="text-violet text-sm hover:underline mt-3 inline-block">← Volver a Academy</Link>
      </div>
    ) : <Loader2 className="w-6 h-6 animate-spin text-violet" />}
  </div></OSLayout>;

  const lessons = course.lessons || [];
  const questions = course.questions || [];
  const cert = course.my_certificate;
  const modules = course.modules && course.modules.length ? course.modules
    : (lessons.length ? [{ title: course.title, lessons }] : []);
  const totalLessons = modules.reduce((n, m) => n + (m.lessons?.length || 0), 0);
  const currentLesson = modules[sel[0]]?.lessons?.[sel[1]];
  const toggleMod = (mi) => setOpenMods((o) => ({ ...o, [mi]: !o[mi] }));

  return (
    <OSLayout title={course.title}>
      <div className="space-y-6">
        <Link to="/os/academia" data-testid="course-back" className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-navy"><ArrowLeft className="w-4 h-4" /> Volver a Academy</Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-violet"><GraduationCap className="w-5 h-5" /><span className="overline text-navy/40">Curso</span></div>
            <h1 className="text-3xl font-medium tracking-tight mt-1">{course.title}</h1>
            {course.description && <p className="text-navy/50 mt-1 text-sm max-w-2xl">{course.description}</p>}
          </div>
          {cert && (
            <button data-testid="course-cert-download" onClick={() => download(cert.id)} className="inline-flex items-center gap-2 rounded-full bg-gold/15 text-gold text-sm px-4 py-2.5 hover:bg-gold/25 transition-colors shrink-0">
              <Award className="w-4 h-4" /> Certificado ({cert.score}%) <Download className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 border-b border-navy/10">
          {[["lessons", `Clases (${totalLessons})`], ["exam", `Examen (${questions.length})`]].map(([k, l]) => (
            <button key={k} data-testid={`course-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-violet text-navy font-medium" : "border-transparent text-navy/50 hover:text-navy"}`}>{l}</button>
          ))}
        </div>

        {tab === "lessons" ? (
          <div className="grid md:grid-cols-[280px_1fr] gap-6" data-testid="course-lessons">
            <div className="space-y-2">
              {modules.map((m, mi) => {
                const open = openMods[mi] ?? false;
                return (
                  <div key={mi} className="border border-navy/10 rounded-lg overflow-hidden bg-white" data-testid={`module-${mi}`}>
                    <button onClick={() => toggleMod(mi)} data-testid={`module-toggle-${mi}`}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-navy/5 transition-colors">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] font-medium text-violet bg-violet/10 rounded px-1.5 py-0.5 shrink-0">M{mi + 1}</span>
                        <span className="text-sm font-medium truncate">{m.title}</span>
                      </span>
                      {open ? <ChevronDown className="w-4 h-4 text-navy/40 shrink-0" /> : <ChevronRight className="w-4 h-4 text-navy/40 shrink-0" />}
                    </button>
                    {open && (
                      <div className="pb-2">
                        {(m.lessons || []).map((l, li) => {
                          const activeSel = sel[0] === mi && sel[1] === li;
                          return (
                            <button key={li} onClick={() => { setSel([mi, li]); setEdit(null); }} data-testid={`lesson-${mi}-${li}`}
                              className={`w-full text-left pl-4 pr-3 py-2 text-sm flex items-center gap-2 transition-colors ${activeSel ? "bg-violet/10 text-violet" : "text-navy/60 hover:bg-navy/5"}`}>
                              <BookOpen className="w-3.5 h-3.5 shrink-0" /> <span className="line-clamp-2">{l.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <motion.div key={`${sel[0]}-${sel[1]}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease }} className="bg-white border border-navy/10 rounded-lg p-6 min-h-[300px]">
              {currentLesson ? (
                edit ? (
                  <div className="space-y-3" data-testid="lesson-editor">
                    <div><label className="text-xs text-navy/50 block mb-1">Título de la clase</label>
                      <input data-testid="lesson-edit-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                        className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm" /></div>
                    <div><label className="text-xs text-navy/50 block mb-1">Contenido (Markdown: # título, ## subtítulo, - lista)</label>
                      <textarea data-testid="lesson-edit-content" rows={16} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })}
                        className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm font-mono leading-relaxed" /></div>
                    <div className="flex items-center gap-2">
                      <button data-testid="lesson-edit-save" onClick={saveLesson} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 disabled:opacity-50">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />} Guardar clase
                      </button>
                      <button data-testid="lesson-edit-cancel" onClick={() => setEdit(null)} className="rounded-full border border-navy/15 text-sm px-5 py-2.5 hover:bg-navy/5">Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="overline text-navy/35 mb-1">Módulo {sel[0] + 1} · {modules[sel[0]]?.title}</div>
                        <h2 className="text-2xl font-medium mb-4">{currentLesson.title}</h2>
                      </div>
                      {isAdmin && (
                        <button data-testid="lesson-edit-btn" onClick={() => setEdit({ title: currentLesson.title, content: currentLesson.content })}
                          className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-navy/15 text-xs px-3 py-1.5 text-navy/60 hover:text-violet hover:border-violet transition-colors">
                          <Pencil className="w-3.5 h-3.5" /> Editar clase
                        </button>
                      )}
                    </div>
                    <div className="prose-omnia space-y-1">{renderContent(currentLesson.content)}</div>
                  </>
                )
              ) : <p className="text-navy/40">Sin clases.</p>}
            </motion.div>
          </div>
        ) : (
          <div className="max-w-3xl" data-testid="course-exam">
            {result ? (
              <div className={`rounded-xl p-8 text-center border ${result.passed ? "bg-emerald-500/5 border-emerald-500/20" : "bg-rose-500/5 border-rose-500/20"}`} data-testid="exam-result">
                {result.passed ? <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" /> : <XCircle className="w-14 h-14 text-rose-500 mx-auto" />}
                <h2 className="text-3xl font-medium mt-4">{result.score}%</h2>
                <p className="text-navy/60 mt-1">{result.correct} de {result.total} correctas · {result.passed ? "Aprobado" : `Necesitas ${result.pass_score}%`}</p>
                <div className="flex items-center justify-center gap-3 mt-6">
                  {result.passed && result.certificate_id ? (
                    <button data-testid="exam-cert-download" onClick={() => download(result.certificate_id)} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90"><Award className="w-4 h-4" /> Descargar certificado</button>
                  ) : (
                    <button data-testid="exam-retry" onClick={retry} className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-5 py-3 hover:bg-navy/5"><RotateCcw className="w-4 h-4" /> Reintentar examen</button>
                  )}
                </div>
              </div>
            ) : questions.length === 0 ? (
              <p className="text-navy/40 py-12 text-center">Este curso aún no tiene examen.</p>
            ) : (
              <div className="space-y-5">
                {questions.map((q, qi) => (
                  <div key={q.id} className="bg-white border border-navy/10 rounded-lg p-5" data-testid={`question-${qi}`}>
                    <p className="font-medium mb-3"><span className="text-violet mr-2">{qi + 1}.</span>{q.q}</p>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => (
                        <label key={oi} data-testid={`q${qi}-opt${oi}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer text-sm transition-colors ${answers[qi] === oi ? "border-violet bg-violet/5" : "border-navy/10 hover:border-navy/25"}`}>
                          <input type="radio" name={`q-${qi}`} checked={answers[qi] === oi} onChange={() => setAnswers({ ...answers, [qi]: oi })} className="accent-violet" />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <button data-testid="exam-submit" onClick={submit} disabled={busy} className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Enviar examen
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </OSLayout>
  );
}
